import { SheetSurface } from "@ui/components/SheetSurface";
import type { ReactNode } from "react";
import { mobileSheet } from "./mobile-sheet.styles";

export interface MobileSheetProps {
  children: ReactNode;
  /** Accessible name for the surface. */
  label: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /**
   * "floating" (iOS): the panel is transparent and sits in gutters, so its
   * contents supply their own cards. "docked" (Material 3): edge-to-edge with
   * a white fill and a rounded top.
   */
  presentation: "floating" | "docked";
  /** Extra classes for the panel. */
  className?: string;
}

/**
 * A transient bottom sheet for mobile: action sheets, reaction pickers, and
 * anything else offering a short list of choices over the current context.
 * Dismissed by swipe, scrim tap, Escape/back, or its own controls.
 *
 * Desktop never reaches this surface — anchored menus and popovers are React
 * Aria's job. See Foundations/Overlays in Storybook.
 */
export function MobileSheet({ children, label, isOpen, onOpenChange, presentation, className }: MobileSheetProps) {
  const styles = mobileSheet({ presentation });

  return (
    <SheetSurface.Root onOpenChange={onOpenChange} isOpen={isOpen}>
      <SheetSurface.View
        backdropClassName={styles.backdrop()}
        className={styles.panel({ class: className })}
        label={label}
        placement="bottom"
      >
        {children}
      </SheetSurface.View>
    </SheetSurface.Root>
  );
}
